import { Box, Divider, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState, ReactElement } from "react";
import { useUser } from "@/app/providers";
import { getRoleTypeNameFromUser } from "@/utils";
import { useLazyQuery } from "@apollo/client/react";
import { GET_ALL_ROLES } from "@/hooks";
import { Link } from "react-router";

/**
 * UserInfoBox component displays user profile information.
 */
export const UserInfoBox = (): ReactElement => {
  const { user } = useUser();
  const [role, setRole] = useState<string>("User");
  const [getRole] = useLazyQuery(GET_ALL_ROLES);

  // Derived primitives to keep effect deps stable
  const currentRoleKey = user?.currentRoleKey;
  const roles = user?.roles;
  const roleType = useMemo(() => getRoleTypeNameFromUser(user), [user]);
  const roleFallback = useMemo(() => {
    if (currentRoleKey) return currentRoleKey;
    if (roles?.length) return roles[0];
    if (roleType) return roleType;
    return "User";
  }, [currentRoleKey, roles, roleType]);

  useEffect(() => {
    let mounted = true;

    const fetchRole = async (): Promise<void> => {
      let selectedRole = "";

      if (currentRoleKey) {
        selectedRole = currentRoleKey;
      } else if (
        roles?.length &&
        (roleType === "super" || roleType === "system")
      ) {
        selectedRole = roles[0];
      }

      if (!selectedRole) {
        if (mounted) setRole(roleFallback);
        return;
      }

      if (mounted) {
        setRole(selectedRole);
      }

      try {
        const { data } = await getRole({ variables: { role: selectedRole } });
        if (!mounted) return;
        setRole(
          (data as any)?.roles?.[0]?.label ||
            (data as any)?.comRoles?.[0]?.label ||
            selectedRole,
        );
      } catch {
        if (mounted) setRole(selectedRole || roleFallback);
      }
    };

    fetchRole();

    return () => {
      mounted = false;
    };
  }, [currentRoleKey, roles, roleType, roleFallback, getRole]);

  if (!user) {
    return <></>;
  }

  // Safe display strings
  const displayName = user?.name || user?.email || "";

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginBottom: 1,
          width: "100%",
        }}
      >
        <Link
          to="/user-account/profile"
          style={{
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                fontSize: "1.25rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: "leftMenu.textColorInActive",
              }}
            >
              {displayName}
            </Typography>
            {/* <Typography
            variant="caption"
            sx={{
              fontSize: "1rem",
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "leftMenu.textColorInActive",
            }}
          >
            {userEmail}
          </Typography> */}
            {role && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: "1rem",
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: "leftMenu.textColorInActive",
                }}
              >
                {role}
              </Typography>
            )}
          </Box>
        </Link>
      </Box>
      <Divider sx={{ marginY: 1 }} />
    </>
  );
};
